import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const SESSION_TOKEN_KEY = 'wwi_session_token';

// Save status types
export const SAVE_STATUS = {
  IDLE: 'idle',
  SAVING: 'saving',
  SAVED: 'saved',
  RETRYING: 'retrying',
  ERROR: 'error'
};

const AuthContext = createContext(null);

// Helper to get auth headers (uses localStorage token as fallback for cookies)
export const getAuthHeaders = () => {
  const token = localStorage.getItem(SESSION_TOKEN_KEY);
  if (token) {
    return { 'Authorization': `Bearer ${token}` };
  }
  return {};
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [saveStatus, setSaveStatus] = useState(SAVE_STATUS.IDLE);
  const [saveError, setSaveError] = useState(null);
  const hasCheckedAuth = useRef(false);
  const saveTimeoutRef = useRef(null);

  // Check authentication status
  const checkAuth = useCallback(async () => {
    // Skip if already authenticated (e.g., just completed OAuth)
    if (isAuthenticated) {
      console.log('[Auth] Already authenticated, skipping checkAuth');
      setLoading(false);
      return;
    }
    
    // Skip if already checked
    if (hasCheckedAuth.current) {
      console.log('[Auth] Already checked auth, skipping');
      return;
    }
    hasCheckedAuth.current = true;
    
    console.log('[Auth] checkAuth called, hash:', window.location.hash?.substring(0, 30));
    
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      console.log('[Auth] Skipping checkAuth - session_id in hash, AuthCallback will handle');
      setLoading(false);
      return;
    }

    // Check if we have a localStorage token
    const storedToken = localStorage.getItem(SESSION_TOKEN_KEY);
    console.log('[Auth] localStorage token exists:', !!storedToken);

    try {
      console.log('[Auth] Checking auth status via /api/auth/check');
      const response = await fetch(`${API_URL}/api/auth/check`, {
        credentials: 'include',
        headers: getAuthHeaders()  // Include localStorage token as fallback
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('[Auth] Auth check response:', data.authenticated, data.user?.email);
        if (data.authenticated && data.user) {
          setUser(data.user);
          setIsAuthenticated(true);
          console.log('[Auth] User authenticated');
        } else {
          // Only clear localStorage token if we had one and server explicitly rejected it
          // This prevents clearing the token due to network issues
          if (storedToken) {
            console.log('[Auth] Server did not recognize token, clearing localStorage');
            localStorage.removeItem(SESSION_TOKEN_KEY);
          }
          setUser(null);
          setIsAuthenticated(false);
          console.log('[Auth] No valid session found');
        }
      } else {
        // Network or server error - don't clear token, might be temporary
        console.log('[Auth] Auth check returned non-OK status:', response.status);
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.log('[Auth] Auth check failed:', error.message);
      // Don't clear token on network error - might be temporary
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Sign in with Google
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const signIn = useCallback(() => {
    const redirectUrl = window.location.origin + '/app';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  // Sign out
  const signOut = useCallback(async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders()
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // Clear localStorage token
    localStorage.removeItem(SESSION_TOKEN_KEY);
    
    // Clear ALL user data from localStorage for a fresh start
    // Main board data (the actual key used in AppPage.jsx)
    localStorage.removeItem('wherewasi_data');
    
    // Other user data keys
    localStorage.removeItem('wwi_board_data');
    localStorage.removeItem('wwi_last_sync');
    localStorage.removeItem('wwi_last_active_task');
    localStorage.removeItem('wwi_fire_counter');
    localStorage.removeItem('wwi_completed_count');
    localStorage.removeItem('wwi_session_count');
    localStorage.removeItem('wwi_column_order');
    localStorage.removeItem('wwi_edit_hint_dismissed');
    localStorage.removeItem('wwi_app_opens');
    localStorage.removeItem('wwi_sounds');
    localStorage.removeItem('wwi_animations');
    localStorage.removeItem('wwi_celebrations');
    localStorage.removeItem('wwi_share_app_dismissed');
    
    // Clear sessionStorage too
    sessionStorage.removeItem('wwi_current_session');
    
    setUser(null);
    setIsAuthenticated(false);
    
    // Redirect to home
    window.location.href = '/';
  }, []);

  // Exchange session_id for session_token (called from AuthCallback)
  const exchangeSession = useCallback(async (sessionId) => {
    console.log('[Auth] Starting session exchange with sessionId:', sessionId?.substring(0, 10) + '...');
    try {
      const response = await fetch(`${API_URL}/api/auth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      });
      
      console.log('[Auth] Session exchange response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`Session exchange failed: ${response.status}`);
      }

      const userData = await response.json();
      console.log('[Auth] Session exchange successful, user:', userData?.email);
      
      // Store session token in localStorage as backup for cookie issues (mobile Safari, etc.)
      if (userData.session_token) {
        localStorage.setItem(SESSION_TOKEN_KEY, userData.session_token);
        console.log('[Auth] Session token stored in localStorage');
        // Remove token from user object before setting state (don't expose in UI)
        const { session_token, ...userWithoutToken } = userData;
        setUser(userWithoutToken);
      } else {
        setUser(userData);
      }
      
      setIsAuthenticated(true);
      console.log('[Auth] State updated: isAuthenticated = true');
      return userData;
    } catch (error) {
      console.error('[Auth] Session exchange error:', error);
      throw error;
    }
  }, []);

  // Fetch user's saved data from cloud
  const fetchUserData = useCallback(async () => {
    if (!isAuthenticated) return null;
    
    try {
      const response = await fetch(`${API_URL}/api/user/data`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const result = await response.json();
        return result.hasData ? result.data : null;
      }
    } catch (error) {
      console.error('Fetch user data error:', error);
    }
    return null;
  }, [isAuthenticated]);

  // Save user data to cloud with status tracking
  const saveUserData = useCallback(async (boards, fireCounter, settings) => {
    if (!isAuthenticated) return { success: false, status: 'not_authenticated' };
    
    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    setSaveStatus(SAVE_STATUS.SAVING);
    setSaveError(null);
    
    // Frontend retry logic (in addition to backend retries)
    const maxRetries = 2;
    let lastError = null;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        if (attempt > 0) {
          setSaveStatus(SAVE_STATUS.RETRYING);
          // Wait before retry
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        }
        
        const response = await fetch(`${API_URL}/api/user/data`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          credentials: 'include',
          body: JSON.stringify({
            boards,
            fire_counter: fireCounter,
            settings
          })
        });
        
        if (response.ok) {
          setSaveStatus(SAVE_STATUS.SAVED);
          // Reset to idle after 2 seconds
          saveTimeoutRef.current = setTimeout(() => {
            setSaveStatus(SAVE_STATUS.IDLE);
          }, 2000);
          return { success: true, status: 'saved' };
        }
        
        // Handle specific error codes
        if (response.status === 503) {
          // Server overloaded, worth retrying
          lastError = 'Service temporarily unavailable';
          continue;
        }
        
        // Other errors - don't retry
        const errorData = await response.json().catch(() => ({}));
        lastError = errorData.detail || 'Save failed';
        break;
        
      } catch (error) {
        console.error(`Save attempt ${attempt + 1} failed:`, error);
        lastError = error.message || 'Network error';
        
        // Network errors are worth retrying
        if (attempt < maxRetries) {
          continue;
        }
      }
    }
    
    // All attempts failed
    setSaveStatus(SAVE_STATUS.ERROR);
    setSaveError(lastError);
    
    // Keep error visible for 5 seconds, then go back to idle
    saveTimeoutRef.current = setTimeout(() => {
      setSaveStatus(SAVE_STATUS.IDLE);
      setSaveError(null);
    }, 5000);
    
    return { success: false, status: 'error', error: lastError };
  }, [isAuthenticated]);
  
  // Clear save status (for manual dismissal)
  const clearSaveStatus = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    setSaveStatus(SAVE_STATUS.IDLE);
    setSaveError(null);
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated,
    saveStatus,
    saveError,
    signIn,
    signOut,
    exchangeSession,
    fetchUserData,
    saveUserData,
    clearSaveStatus,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
